/**
 * React Query hooks for contributor asset-library management.
 *
 * Endpoints:
 *   GET    /contributor/arches/:archeId/asset-library/config
 *   POST   /contributor/arches/:archeId/asset-library/{enable,disable}
 *   GET    /contributor/arches/:archeId/billing/summary
 *   GET    /contributor/arches/:archeId/assets
 *   POST   /contributor/arches/:archeId/assets/upload-init
 *   POST   /contributor/arches/:archeId/assets/:assetId/finalize
 *   PATCH  /contributor/arches/:archeId/assets/:assetId
 *   POST   /contributor/arches/:archeId/assets/:assetId/soft-delete
 *   POST   /contributor/arches/:archeId/manifests/snapshot
 *   GET    /contributor/arches/:archeId/assets?source=shared   (ADR-019)
 *   GET    /contributor/arches/:archeId/workspaces              (ADR-019)
 *   POST   /contributor/workspaces/:holderId/arches/:archeId    link (ADR-019)
 *   DELETE /contributor/workspaces/:holderId/arches/:archeId    soft unlink (ADR-019)
 */

import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ContributorArcheAsset } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { assetContentTypeForFile } from '@/contributor/components/asset-library/format';

export function useArcheAssetLibraryConfig(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.libraryConfig(archeId),
    queryFn: () => api.getArcheAssetLibraryConfig(archeId),
    enabled: !!archeId,
  });
}

export function useArcheAssetBillingSummary(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.billingSummary(archeId),
    queryFn: () => api.getArcheAssetBillingSummary(archeId),
    enabled: !!archeId,
  });
}

export function useArcheAssetsList(
  archeId: string,
  opts?: {
    status?: string;
    tag?: string;
    cursor?: string;
    limit?: number;
    sort?: import('@/lib/api').AssetListSortValue;
    q?: string;
  },
) {
  return useQuery({
    queryKey: queryKeys.contributorArches.assets(archeId, opts as Record<string, unknown>),
    queryFn: () => api.listArcheAssets(archeId, opts),
    enabled: !!archeId,
  });
}

export function useEnableArcheAssetLibrary(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.enableArcheAssetLibrary(archeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

export function useDisableArcheAssetLibrary(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.disableArcheAssetLibrary(archeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

/**
 * Push the current draft library live on an already-published arche.
 *
 * The plugin runtime reads the published MANIFEST, not the asset rows, so
 * an upload is invisible to a live arche until something snapshots it.
 * Publishing the arche does that automatically; this is the standalone
 * path for "I only added art".
 */
export function useSnapshotArcheAssetManifest(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.snapshotArcheAssetManifest(archeId, true),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.billingSummary(archeId) });
    },
  });
}

export interface UploadProgress {
  file: File;
  status: 'pending' | 'uploading' | 'finalizing' | 'done' | 'error';
  errorMessage?: string;
  asset?: ContributorArcheAsset;
}

/**
 * Performs the 3-step upload (init → S3 PUT → finalize) for one
 * file. Returns the finalized asset row. Components track multi-
 * file batches by calling this once per file with their own
 * progress UI; per-batch concurrency is the caller's concern.
 */
export function useUploadArcheAsset(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; tags?: string[] }) => {
      const contentType = assetContentTypeForFile(input.file);
      if (!contentType) {
        throw new Error('Unsupported file type — use PNG, JPEG, WebP, MP3 or WAV');
      }
      const init = await api.initArcheAssetUpload(archeId, {
        contentType,
        sizeBytes: input.file.size,
        name: input.file.name,
        tags: input.tags,
      });

      // Direct S3 PUT — no auth header (URL is presigned).
      const putRes = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': contentType,
        },
        body: input.file,
      });
      if (!putRes.ok) {
        throw new Error(`S3 upload failed (${putRes.status})`);
      }

      const fin = await api.finalizeArcheAsset(archeId, init.assetId, {
        name: input.file.name,
        tags: input.tags,
      });
      return fin.asset;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

export function usePatchArcheAsset(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    // The server PATCH schema is `.strict()` — the body must carry ONLY the
    // patchable fields, never the assetId (which travels in the path).
    mutationFn: ({
      assetId,
      ...body
    }: {
      assetId: string;
      name?: string | null;
      altText?: string | null;
      tags?: string[];
    }) => api.patchArcheAsset(archeId, assetId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
    },
  });
}

export function useSoftDeleteArcheAsset(archeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => api.softDeleteArcheAsset(archeId, assetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.assets(archeId) });
      qc.invalidateQueries({ queryKey: queryKeys.contributorArches.libraryConfig(archeId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Workspace (org Holder) shared libraries — the arche side (ADR-019)
// ---------------------------------------------------------------------------

/**
 * The active assets of every workspace library the arche is validly linked
 * to — what a publish would pin from workspaces. The server returns ONLY
 * workspace-owned rows (each carries `holderId`); the arche page renders
 * them read-only. Cursor-paged: `fetchNextPage` follows `nextCursor`.
 */
export function useArcheSharedAssets(archeId: string, opts?: { tag?: string; limit?: number }) {
  return useInfiniteQuery({
    queryKey: queryKeys.contributorArches.sharedAssets(archeId, opts as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      api.listArcheAssets(archeId, {
        ...opts,
        ...(pageParam ? { cursor: pageParam } : {}),
        source: 'shared',
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!archeId,
  });
}

/**
 * Workspace libraries this arche is linked to (each with its derived
 * validity) plus the caller's workspaces that could be linked.
 */
export function useArcheWorkspaceLinks(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.workspaces(archeId),
    queryFn: () => api.getArcheWorkspaceLinks(archeId),
    enabled: !!archeId,
  });
}

function useInvalidateArcheWorkspaceState(archeId: string) {
  const qc = useQueryClient();
  return (holderId: string) => {
    void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.workspaces(archeId) });
    void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.sharedAssets(archeId) });
    void qc.invalidateQueries({ queryKey: queryKeys.workspaceAssetLibrary.arches(holderId) });
  };
}

/** Link this arche to a workspace library (the workspace route; admin+ or self-serve member). */
export function useLinkArcheToWorkspace(archeId: string) {
  const invalidate = useInvalidateArcheWorkspaceState(archeId);
  return useMutation({
    mutationFn: (holderId: string) => api.linkArcheToWorkspace(holderId, archeId),
    onSuccess: (_data, holderId) => invalidate(holderId),
  });
}

/**
 * Soft-unlink from the arche side: the link stops qualifying for future
 * publishes; the live published version keeps serving until the arche
 * republishes. Hard revoke is a workspace-admin action and lives on the
 * workspace page only.
 */
export function useUnlinkArcheFromWorkspace(archeId: string) {
  const invalidate = useInvalidateArcheWorkspaceState(archeId);
  return useMutation({
    mutationFn: (holderId: string) =>
      api.revokeWorkspaceArcheLink(holderId, archeId, { mode: 'soft' }),
    onSuccess: (_data, holderId) => invalidate(holderId),
  });
}
